import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Button, Divider, Typography } from '@mui/material';
import useChangePDAStatus from 'api/operation/activePdaList/changePDAStatus';
import useActivePDAList from 'api/operation/activePdaList/useActivePDAList';
import useSectionList from 'api/operation/activePdaList/useSectionList';
import useUpdateSections from 'api/operation/activePdaList/useUpdateSection';
import useZoneSections from 'api/operation/activePdaList/useZoneSections';
import SectionSelectModal from 'components/private/Modals/activePDAList/SectionSelectModal';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import React, { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';

const PDACard = ({ pda, onClick }) => {
  const isActive = pda.status === 2;

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        minHeight: 140,
        maxWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1.5,
        overflow: 'hidden',
        borderBottom: isActive
          ? '10px solid #4CAF50'
          : '10px solid transparent',
      }}
    >
      <PersonOutlineOutlinedIcon sx={{ fontSize: 48, color: '#9EADB4' }} />
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: 15,
          color: '#434343',
          textAlign: 'center',
        }}
      >
        {pda.fullname}
      </Typography>
    </Box>
  );
};

const PDADetailsCard = ({
  pda,
  sections,
  sectionsLoading,
  statusLoading,
  onToggleStatus,
  onEditSections,
}) => {
  if (!pda) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Typography>لطفا یک PDA را انتخاب کنید</Typography>
      </Box>
    );
  }

  const isActive = pda.status === 2;

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        minHeight: '100%',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#434343' }}>
          کاربر:
        </Typography>
        <Typography sx={{ fontWidth: 300 }}>{pda.fullname}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 15, color: '#434343' }}>وضعیت</Typography>
        <Button
          variant="contained"
          color={isActive ? 'success' : 'error'}
          disableElevation
          disabled={statusLoading}
          onClick={onToggleStatus}
        >
          {isActive ? 'فعال' : 'غیرفعال'}
        </Button>
      </Box>

      <Divider />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 15, color: '#434343' }}>
          طبقات فعال
        </Typography>
        <Button
          color="warning"
          variant="contained"
          disableElevation
          onClick={onEditSections}
        >
          ویرایش طبقات
        </Button>
      </Box>

      {sectionsLoading ? (
        <SpinnerLoader />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {sections.length === 0 ? (
            <Typography sx={{ color: '#9EADB4', fontSize: 14 }}>
              طبقه‌ای اختصاص داده نشده است
            </Typography>
          ) : (
            sections.map(section => (
              <Box
                key={section.guid}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: '#F0F2F3',
                    borderRadius: '8px',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: '#434343' }}>
                    {section.display}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

const ActivePDAListPage = () => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = useMemo(() => {
    return operationSettings?.defaultZone?.key;
  }, [operationSettings?.defaultZone?.key]);

  const { getActivePDAList, loading, activePDAList } = useActivePDAList();
  const { changePDAStatus, loading: statusLoading } = useChangePDAStatus();
  const [getSectionList, sectionsLoading, assignedSections] = useSectionList();
  const { getZoneSections, zoneSections } = useZoneSections();
  const { updateSections, loading: updateLoading } = useUpdateSections();

  const [selectedPda, setSelectedPda] = useState(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);

  const selectedGuids = assignedSections.map(section => section.guid);

  useEffect(() => {
    if (!zoneGuid) return;
    getActivePDAList({ guid: zoneGuid });
    getZoneSections({ zoneGuid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneGuid]);

  useEffect(() => {
    if (activePDAList.length > 0 && !selectedPda) {
      setSelectedPda(activePDAList[0]);
    }
  }, [activePDAList, selectedPda]);

  useEffect(() => {
    if (selectedPda?.guid) {
      getSectionList({ guid: selectedPda.guid });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPda?.guid]);

  useEffect(() => {
    if (!selectedPda?.guid || activePDAList.length === 0) return;
    const updated = activePDAList.find(item => item.guid === selectedPda.guid);
    if (updated && updated.status !== selectedPda.status) {
      setSelectedPda(updated);
    }
  }, [activePDAList, selectedPda]);

  const handleSelectPda = pda => {
    setSelectedPda(pda);
  };

  const handleToggleStatus = () => {
    if (!selectedPda?.guid) return;

    const nextStatus = selectedPda.status === 2 ? 1 : 2;

    changePDAStatus({ guid: selectedPda.guid, status: nextStatus }, () => {
      getActivePDAList({ guid: zoneGuid });
    });
  };

  const handleEditSections = () => {
    setIsSectionModalOpen(true);
  };

  const handleSubmitSections = checkedGuids => {
    if (!selectedPda?.guid) return;

    updateSections(
      { guid: selectedPda.guid, sectionsGuid: checkedGuids },
      () => {
        getSectionList({ guid: selectedPda.guid });
        setIsSectionModalOpen(false);
      },
    );
  };

  return (
    <RequireOperationSettings>
      {loading ? (
        <PageSpinner loading />
      ) : activePDAList.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>داده‌ای یافت نشد.</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              'display': 'flex',
              'justifyContent': 'center',
              'alignItems': 'flex-start',
              'gap': 2,
              'width': '100%',
              'direction': 'rtl',
              '@media (max-width: 1200px)': {
                flexDirection: 'column',
              },
            }}
          >
            <Box
              sx={{
                'width': '45%',
                'height': 'calc(100vh - 135px)',
                'overflow': 'auto',
                'px': 1,
                'py': 3,
                '@media (max-width: 1200px)': {
                  width: '100%',
                  height: 'auto',
                },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr',
                    md: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  },

                  gap: 2,
                }}
              >
                {activePDAList.map(item => (
                  <PDACard
                    key={item.guid}
                    pda={item}
                    onClick={() => handleSelectPda(item)}
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                'width': '55%',
                'height': 'calc(100vh - 135px)',
                'overflow': 'auto',
                '@media (max-width: 1200px)': {
                  width: '100%',
                  height: 'auto',
                },
              }}
            >
              <PDADetailsCard
                pda={selectedPda}
                sections={assignedSections}
                sectionsLoading={sectionsLoading}
                statusLoading={statusLoading}
                onToggleStatus={handleToggleStatus}
                onEditSections={handleEditSections}
              />
            </Box>
          </Box>

          <SectionSelectModal
            open={isSectionModalOpen}
            handleClose={() => setIsSectionModalOpen(false)}
            sections={zoneSections}
            selectedGuids={selectedGuids}
            onSubmit={handleSubmitSections}
            loading={updateLoading}
          />
        </>
      )}
    </RequireOperationSettings>
  );
};

export default ActivePDAListPage;
